import { useState, useCallback, useRef } from 'react';
// @ts-ignore - Cesium module resolution issue
import * as Cesium from 'cesium';
// Cesium Ion Asset IDs
const BING_ARIAL_ASSET_ID = 2;
const BING_ARIAL_LABELS_ASSET_ID = 3;
export const useBasemap = (viewer, loadedArtifacts, updateLoadedArtifact, addStatus) => {
    const [currentBasemap, setCurrentBasemap] = useState('bing-aerial');
    const viewerRef = useRef(viewer);
    // Update ref when viewer changes
    viewerRef.current = viewer;
    const changeBasemap = useCallback(async (basemap) => {
        const currentViewer = viewerRef.current;
        if (!currentViewer)
            return;
        // Preserve loaded raster and vector artifacts before removing layers
        const rasterArtifacts = [];
        const vectorArtifacts = [];
        loadedArtifacts.forEach((artifact) => {
            if (artifact.type === 'raster' && artifact.cesiumObject) {
                rasterArtifacts.push({ artifact, imageryLayer: artifact.cesiumObject });
            }
            else if (artifact.type === 'vector' && artifact.cesiumObject) {
                vectorArtifacts.push({ artifact, dataSource: artifact.cesiumObject });
            }
        });
        // Remove all existing imagery layers and data sources
        currentViewer.imageryLayers.removeAll();
        // Temporarily remove vector data sources (they'll be re-added after basemap loads)
        vectorArtifacts.forEach(({ dataSource }) => {
            currentViewer.dataSources.remove(dataSource);
        });
        // Force a scene render to clear any cached tiles from previous basemap
        currentViewer.scene.requestRender();
        // Configure Cesium Ion access token - clear any existing token first
        Cesium.Ion.defaultAccessToken = '';
        Cesium.Ion.defaultAccessToken = REACT_APP_CESIUM_ION_TOKEN;
        // Add the new basemap
        let imageryProvider;
        switch (basemap) {
            case 'bing-aerial':
                imageryProvider = await Cesium.IonImageryProvider.fromAssetId(BING_ARIAL_ASSET_ID, {
                    accessToken: REACT_APP_CESIUM_ION_TOKEN
                });
                break;
            case 'bing-aerial-labels':
                imageryProvider = await Cesium.IonImageryProvider.fromAssetId(BING_ARIAL_LABELS_ASSET_ID, {
                    accessToken: REACT_APP_CESIUM_ION_TOKEN
                });
                break;
            case 'usgs-topo':
                imageryProvider = new Cesium.UrlTemplateImageryProvider({
                    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}',
                    credit: 'Tile data from USGS',
                    minimumLevel: 0,
                    maximumLevel: 16,
                    tilingScheme: new Cesium.WebMercatorTilingScheme(),
                    tileWidth: 256,
                    tileHeight: 256
                });
                break;
            case 'carto-light':
                imageryProvider = new Cesium.UrlTemplateImageryProvider({
                    url: 'https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
                    credit: '© CARTO',
                    minimumLevel: 0,
                    maximumLevel: 18,
                    tilingScheme: new Cesium.WebMercatorTilingScheme(),
                    tileWidth: 256,
                    tileHeight: 256
                });
                break;
            case 'carto-dark':
                imageryProvider = new Cesium.UrlTemplateImageryProvider({
                    url: 'https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
                    credit: '© CARTO',
                    minimumLevel: 0,
                    maximumLevel: 18,
                    tilingScheme: new Cesium.WebMercatorTilingScheme(),
                    tileWidth: 256,
                    tileHeight: 256
                });
                break;
        }
        if (imageryProvider) {
            // Add the basemap layer first
            const basemapLayer = currentViewer.imageryLayers.addImageryProvider(imageryProvider);
            // Wait for initial tiles to load before re-adding artifacts to prevent visual conflicts
            const checkBasemapReady = () => {
                setTimeout(() => {
                    // Re-add the preserved raster artifacts
                    rasterArtifacts.forEach(({ artifact, imageryLayer }) => {
                        var _a;
                        try {
                            // Create new imagery layer from the same provider
                            const newImageryLayer = currentViewer.imageryLayers.addImageryProvider(imageryLayer.imageryProvider);
                            newImageryLayer.alpha = imageryLayer.alpha; // Preserve opacity
                            newImageryLayer.show = imageryLayer.show; // Preserve visibility
                            // Update the artifact reference to the new layer
                            const artifactId = (_a = Array.from(loadedArtifacts.entries()).find(([_, a]) => a === artifact)) === null || _a === void 0 ? void 0 : _a[0];
                            if (artifactId) {
                                updateLoadedArtifact(artifactId, {
                                    ...artifact,
                                    cesiumObject: newImageryLayer
                                });
                            }
                        }
                        catch (error) {
                            addStatus(`❌ Error restoring raster artifact: ${artifact.metadata.getName()}`);
                        }
                    });
                    // Re-add the preserved vector artifacts
                    vectorArtifacts.forEach(({ artifact, dataSource }) => {
                        try {
                            // Re-add the data source to the viewer
                            currentViewer.dataSources.add(dataSource);
                            // The artifact reference doesn't need updating since dataSource object remains the same
                        }
                        catch (error) {
                            addStatus(`❌ Error restoring vector artifact: ${artifact.metadata.getName()}`);
                        }
                    });
                }, 100); // Small delay to allow basemap tiles to start loading
            };
            checkBasemapReady();
            setCurrentBasemap(basemap);
            addStatus(`🗺️ Changed basemap to ${basemap}, preserved ${rasterArtifacts.length} raster and ${vectorArtifacts.length} vector artifacts`);
        }
    }, [loadedArtifacts, updateLoadedArtifact, addStatus]);
    return {
        currentBasemap,
        changeBasemap
    };
};
